import React from "react";

interface CatchAiLogoProps {
  className?: string;
  size?: number;
  showText?: boolean;
  textClassName?: string;
}

/**
 * CatchAI Text Official Vector SVG Logo & Mark
 * Clean, balanced vector representation designed for perfect legibility across all display sizes.
 * Features an authentic document glyph encased in precision verification aperture brackets.
 */
export const CatchAiLogo: React.FC<CatchAiLogoProps> = ({
  className = "",
  size = 32,
  showText = false,
  textClassName = "",
}) => {
  return (
    <div className={`inline-flex items-center gap-2.5 select-none ${className}`}>
      {/* Pure Vector SVG Mark */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0 transition-transform duration-200 group-hover:scale-105"
        role="img"
        aria-label="CatchAI Text Logo"
      >
        <defs>
          {/* Main Brand Gradient: Deep Royal Indigo to Midnight Violet */}
          <linearGradient id="catchAiBgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="45%" stopColor="#4338ca" />
            <stop offset="100%" stopColor="#1e1b4b" />
          </linearGradient>

          {/* Verification Checkmark Accent Gradient: Luminous Cyan to Emerald */}
          <linearGradient id="catchAiTargetGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="55%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>

        {/* Circular Housing with inner glass border */}
        <circle cx="20" cy="20" r="20" fill="url(#catchAiBgGrad)" />
        <circle
          cx="20"
          cy="20"
          r="18.8"
          stroke="#ffffff"
          strokeOpacity="0.18"
          strokeWidth="0.8"
        />

        {/* Big Bold Verification Checkmark */}
        <path
          d="M 10.6 20.9 L 17.3 27.6 L 29.4 12.4"
          fill="none"
          stroke="#ffffff"
          strokeWidth="4.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {/* Styled Brand Typography (when showText is true) */}
      {showText && (
        <span className={`font-display font-bold tracking-tight text-slate-900 dark:text-white leading-none ${textClassName || "text-lg sm:text-xl"}`}>
          CatchAI <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">Text</span>
        </span>
      )}
    </div>
  );
};

export default CatchAiLogo;
